import { useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, Copy, Eye, FileText, Link2, PackagePlus, Send } from 'lucide-react'
import {
  Button,
  Chip,
  Input,
  Modal,
  PageHeaderBand,
  QueryErrorState,
  Textarea,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { documentUrl } from '@/shell/services/cofa-registry/utils/document-url'
import {
  useImportVendorRequest,
  useNewVendorLink,
  useReopenVendorRequest,
  useReviewVendorUpload,
  useRevokeVendorRequest,
  useVendorRequest,
} from '../api/vendor-request-queries'
import type { VendorRequestDetail, VendorUpload } from '../api/vendor-request-types'
import { formatDate } from '../utils/dates'
import { EDIT_ROLES, VENDOR_STATUS_LABEL, VENDOR_STATUS_VARIANT } from '../utils/onboarding-meta'

const REVIEW_ROLES = [...EDIT_ROLES, 'regulatory'] as const

/** One vendor product request: what the vendor sent, document review, import. */
export function VendorRequestDetailPage() {
  const params = useParams({ strict: false }) as { requestId?: string }
  const { data, isLoading, isError, error, refetch } = useVendorRequest(params.requestId ?? null)
  if (isError) {
    return <div className="p-6"><QueryErrorState error={error} onRetry={() => void refetch()} subject="vendor request" /></div>
  }
  if (isLoading || !data) return <div className="p-6 text-secondary">Loading…</div>
  return <Detail r={data} />
}

function Detail({ r }: { r: VendorRequestDetail }) {
  const navigate = useNavigate()
  const canEdit = useHasAnyPermission([...EDIT_ROLES])
  const canReview = useHasAnyPermission([...REVIEW_ROLES])
  const newLink = useNewVendorLink()
  const reopen = useReopenVendorRequest()
  const revoke = useRevokeVendorRequest()
  const doImport = useImportVendorRequest()
  const [link, setLink] = useState<string | null>(null)
  const [reopenNote, setReopenNote] = useState('')
  const [showReopen, setShowReopen] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  const fail = (title: string) => (err: unknown) => toast.danger(title, { description: (err as Error).message })
  const pending = r.uploads.filter((u) => u.review_status === 'pending').length

  return (
    <div data-density="comfortable" data-surface="form" className="flex h-full flex-col">
      <PageHeaderBand title={`${r.vendor_name}: ${r.product_description}`}
        description="Product information sent by the vendor through the request link.">
        <div className="ml-auto flex items-center gap-2">
          <Chip variant="neutral">{r.reference}</Chip>
          <Chip variant={VENDOR_STATUS_VARIANT[r.status]} dot>{VENDOR_STATUS_LABEL[r.status]}</Chip>
        </div>
      </PageHeaderBand>
      <Toolbar>
        <ToolbarLeft>
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/products', search: { tab: 'vendor-requests' } as never })}>
            <ArrowLeft size={14} /> Vendor requests
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <Button variant="ghost" size="sm"
            onClick={() => window.open(`/vendor-request/preview/${r.id}`, '_blank', 'noopener')}>
            <Eye size={14} /> Preview what the vendor sees
          </Button>
          {canEdit && r.status === 'open' ? (
            <Button variant="secondary" size="sm" loading={newLink.isPending}
              onClick={() => newLink.mutate({ id: r.id }, { onSuccess: (res) => setLink(res.link), onError: fail('Could not create a new link') })}>
              <Link2 size={14} /> New link
            </Button>
          ) : null}
          {canEdit && (r.status === 'vendor_submitted' || r.status === 'revoked') ? (
            <Button variant="secondary" size="sm" onClick={() => setShowReopen((v) => !v)}>
              <Send size={14} /> Send back to vendor
            </Button>
          ) : null}
          {canEdit && (r.status === 'open' || r.status === 'vendor_submitted') ? (
            <Button variant="ghost" size="sm" onClick={() => setConfirmRevoke(true)}>Cancel request</Button>
          ) : null}
          {canEdit && r.status === 'vendor_submitted' ? (
            <Button variant="primary" size="sm" disabled={pending > 0} loading={doImport.isPending}
              title={pending ? 'Accept or reject every document first' : undefined}
              onClick={() => doImport.mutate(r.id, {
                onSuccess: (res) => {
                  toast.success(`Imported as ${res.reference}`)
                  navigate({ to: '/products/onboarding/$intakeId', params: { intakeId: res.intake_id } })
                },
                onError: fail('Import failed'),
              })}>
              <PackagePlus size={14} /> Import to new products
            </Button>
          ) : null}
          {r.status === 'imported' && r.intake_id ? (
            <Button variant="secondary" size="sm"
              onClick={() => navigate({ to: '/products/onboarding/$intakeId', params: { intakeId: r.intake_id! } })}>
              Open the new product request
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto bg-canvas px-6 py-6">
        <div className="mx-auto flex w-full max-w-[calc(var(--form-content-width)+var(--form-aside-width)+1.5rem)] items-start gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          {link ? <LinkBox link={link} /> : null}
          {showReopen ? (
            <Section title="Send back to the vendor">
              <p className="text-md text-secondary">The vendor gets a new link (the old one stops working) and sees your note at the top of the form.</p>
              <Textarea rows={3} value={reopenNote} maxLength={2000} onChange={(e) => setReopenNote(e.target.value)}
                placeholder="What needs fixing" />
              <div>
                <Button variant="primary" size="sm" disabled={!reopenNote.trim()} loading={reopen.isPending}
                  onClick={() => reopen.mutate({ id: r.id, note: reopenNote }, {
                    onSuccess: (res) => { setLink(res.link); setShowReopen(false); setReopenNote('') },
                    onError: fail('Could not send it back'),
                  })}>
                  Create new link
                </Button>
              </div>
            </Section>
          ) : null}
          {r.message_to_vendor ? (
            <Section title="Note shown to the vendor"><p className="text-md">{r.message_to_vendor}</p></Section>
          ) : null}
          {r.status === 'open' ? (
            <p className="text-md text-secondary">
              Waiting for the vendor. The link works until {formatDate(r.expires_at)}. Answers below are what they've saved so far.
            </p>
          ) : null}
          {r.schema.sections.map((s) => (
            <Section key={s.key} title={s.title}>
              <dl className="grid grid-cols-[minmax(200px,1fr)_2fr] gap-x-6 gap-y-3 text-md">
                {s.fields.map((f) => (
                  <Row key={f.key} label={f.label} value={r.answers[f.key]} />
                ))}
              </dl>
            </Section>
          ))}
          {r.attestation ? (
            <Section title="Signed by">
              <p className="text-md">{r.attestation.name}, {r.attestation.title} · {formatDate(r.attestation.date)}</p>
            </Section>
          ) : null}
        </div>
        <aside className="sticky top-0 flex max-h-[calc(100vh-12rem)] w-[var(--form-aside-width)] shrink-0 flex-col gap-4 overflow-auto">
          <Section title={pending ? `Documents: ${pending} to review` : 'Documents'}>
            {r.requested_doc_types.map((t) => {
              const u = r.uploads.find((x) => x.doc_type_key === t.key)
              return <UploadReview key={t.key} requestId={r.id} label={t.label} upload={u}
                canReview={canReview && r.status === 'vendor_submitted'} />
            })}
          </Section>
          <Section title="Activity">
            <ol className="flex flex-col gap-1 text-sm">
              {r.events.slice(0, 40).map((e, i) => (
                <li key={i} className="text-secondary">
                  {e.event.replace(/_/g, ' ')} · {e.actor ?? '—'}{e.ip ? ` · ${e.ip}` : ''}
                  <span className="block text-xs text-tertiary">
                    {new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                </li>
              ))}
            </ol>
          </Section>
        </aside>
        </div>
      </div>

      <Modal
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        tone="danger"
        icon={AlertTriangle}
        title="Cancel this request?"
        description="The link stops working immediately. Anything the vendor sent stays here for reference."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmRevoke(false)}>Back</Button>
            <Button variant="danger" loading={revoke.isPending}
              onClick={() => revoke.mutate(r.id, { onSuccess: () => { setConfirmRevoke(false); toast.success('Request cancelled') }, onError: fail('Cancel failed') })}>
              Cancel request
            </Button>
          </div>
        }
      />
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border-default bg-surface p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-primary">{title}</h2>
      {children}
    </section>
  )
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (value.length && typeof value[0] === 'object') {
      return (value as { min_quantity: number; price: number }[]).map((t) => `from ${t.min_quantity}: ${t.price}`).join(' · ')
    }
    return (value as string[]).join(', ')
  }
  return String(value)
}

function Row({ label, value }: { label: string; value: unknown }) {
  return (
    <>
      <dt className="text-secondary">{label}</dt>
      <dd className={value === undefined || value === null || value === '' ? 'text-tertiary' : undefined}>{show(value)}</dd>
    </>
  )
}

function LinkBox({ link }: { link: string }) {
  return (
    <Section title="New link: copy it now, it's shown only once">
      <div className="flex items-center gap-2">
        <Input readOnly value={link} aria-label="Vendor link" onFocus={(e) => e.currentTarget.select()} />
        <Button variant="secondary" onClick={() => void navigator.clipboard.writeText(link).then(() => toast.success('Link copied'))}>
          <Copy size={14} /> Copy
        </Button>
      </div>
    </Section>
  )
}

const REVIEW_VARIANT = { pending: 'warning', accepted: 'success', rejected: 'danger' } as const

function UploadReview({ requestId, label, upload, canReview }: { requestId: string; label: string; upload?: VendorUpload; canReview: boolean }) {
  const review = useReviewVendorUpload()
  const [rejecting, setRejecting] = useState(false)
  const [note, setNote] = useState('')
  const act = (status: 'accepted' | 'rejected') => {
    if (!upload) return
    review.mutate({ id: requestId, uploadId: upload.id, status, note }, {
      onSuccess: () => { setRejecting(false); setNote('') },
      onError: (err) => toast.danger('Review failed', { description: (err as Error).message }),
    })
  }
  return (
    <div className="flex flex-col gap-1 border-b border-border-subtle pb-2 text-sm last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="font-medium">{label}</span>
        {upload ? <Chip variant={REVIEW_VARIANT[upload.review_status]} className="ml-auto">{upload.review_status}</Chip>
          : <span className="ml-auto text-tertiary">not sent</span>}
      </div>
      {upload?.document_id ? (
        <a className="inline-flex items-center gap-1 text-accent-text hover:underline" target="_blank" rel="noopener noreferrer"
          href={documentUrl(upload.document_id, upload.original_filename)}>
          <FileText size={12} /> {upload.original_filename ?? 'document.pdf'}
        </a>
      ) : null}
      {upload?.not_available_reason ? <span className="text-secondary">Not available: {upload.not_available_reason}</span> : null}
      {upload && (upload.issued_on || upload.expires_on) ? (
        <span className="text-xs text-tertiary">Issued {formatDate(upload.issued_on)} · Expires {formatDate(upload.expires_on)}</span>
      ) : null}
      {upload?.review_note ? <span className="text-xs text-tertiary">{upload.reviewed_by}: {upload.review_note}</span> : null}
      {canReview && upload ? (
        rejecting ? (
          <div className="flex items-end gap-2">
            <Input value={note} placeholder="Why it's rejected" onChange={(e) => setNote(e.target.value)} />
            <Button variant="danger" size="sm" disabled={!note.trim()} loading={review.isPending} onClick={() => act('rejected')}>Reject</Button>
            <Button variant="ghost" size="sm" onClick={() => setRejecting(false)}>Back</Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" loading={review.isPending} onClick={() => act('accepted')}>Accept</Button>
            <Button variant="ghost" size="sm" onClick={() => setRejecting(true)}>Reject…</Button>
          </div>
        )
      ) : null}
    </div>
  )
}
